import { logger } from '../utils/logger';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { makeAuthenticatedRequest, handleApiError } from '../utils/api';
import { Character } from '../types/character';
import { CharacterFullStats, EvolutionEligibility } from '../types/skillTree';
import SkillTree from './SkillTree';
import { useConfirm } from './ConfirmModal';
import './CharacterProgression.css';

interface CharacterProgressionProps {
  characterId: number;
  onBack?: () => void;
}

interface TownBonus {
  townName: string;
  townType: string;
  bonusType: string;
  bonusName: string;
  description: string;
  bonusValue: number;
  slot: number;
  tier: number;
}

const CharacterProgression: React.FC<CharacterProgressionProps> = ({ characterId, onBack }) => {
  const { token } = useAuth();
  const { confirm, confirmDialog } = useConfirm();
  const [character, setCharacter] = useState<Character | null>(null);
  const [fullStats, setFullStats] = useState<CharacterFullStats | null>(null);
  const [activeTab, setActiveTab] = useState<'skills' | 'stats' | 'evolution'>('skills');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [townBonuses, setTownBonuses] = useState<TownBonus[]>([]);
  const [evolutions, setEvolutions] = useState<EvolutionEligibility[]>([]);
  const [evolutionsLoading, setEvolutionsLoading] = useState(false);
  const [evolvingId, setEvolvingId] = useState<number | null>(null);
  const [evolutionError, setEvolutionError] = useState<string | null>(null);

  useEffect(() => {
    loadCharacterData();
    loadFullStats();
    loadTownBonuses();
    loadEvolutions();
  }, [characterId]);

  const loadEvolutions = async () => {
    try {
      setEvolutionsLoading(true);
      const response = await makeAuthenticatedRequest(`/skilltree/evolutions/${characterId}`);
      if (response.ok) {
        setEvolutions(await response.json());
      }
    } catch (err) {
      logger.warn('Error loading class evolutions:', err);
    } finally {
      setEvolutionsLoading(false);
    }
  };

  const handleEvolve = async (evolution: EvolutionEligibility) => {
    const confirmed = await confirm({
      title: `Evolve into ${evolution.advancedClass}?`,
      message: `${character?.name} will permanently become a ${evolution.advancedClass}. This gains a permanent stat bonus and cannot be undone.`,
      confirmText: 'Evolve',
    });
    if (!confirmed) return;

    setEvolvingId(evolution.evolutionId);
    setEvolutionError(null);
    try {
      const response = await makeAuthenticatedRequest(`/skilltree/evolutions/${characterId}/evolve`, {
        method: 'POST',
        body: JSON.stringify({ EvolutionId: evolution.evolutionId }),
      });
      if (response.ok) {
        await loadCharacterData();
        await loadFullStats();
        await loadEvolutions();
      } else {
        const errorData = await response.json();
        setEvolutionError(errorData.error || 'Failed to evolve character');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      setEvolutionError(apiError.message);
    } finally {
      setEvolvingId(null);
    }
  };

  const loadCharacterData = async () => {
    try {
      const response = await makeAuthenticatedRequest(`/characters/${characterId}`);

      if (!response.ok) {
        let errorMessage = 'Failed to load character';
        const textResponse = await response.clone().text();
        try {
          const errorData = JSON.parse(textResponse);
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          errorMessage = textResponse || `Server error: ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setCharacter(data);
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError.message);
    }
  };

  const loadFullStats = async () => {
    try {
      setLoading(true);
      const response = await makeAuthenticatedRequest(`/skilltree/character/${characterId}/full-stats`);

      if (!response.ok) {
        let errorMessage = 'Failed to load character stats';
        const textResponse = await response.clone().text();
        try {
          const errorData = JSON.parse(textResponse);
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          errorMessage = textResponse || `Server error: ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setFullStats(data);
      setError(null);
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const loadTownBonuses = async () => {
    try {
      const response = await makeAuthenticatedRequest('/town/bonuses/active');

      if (!response.ok) {
        logger.warn('Failed to load town bonuses');
        return;
      }

      const data = await response.json();
      setTownBonuses(data);
    } catch (err) {
      logger.warn('Error loading town bonuses:', err);
    }
  };

  const renderStatsTab = () => {
    if (!character || !fullStats) return null;

    return (
      <div className="character-stats-container">
        <div className="character-info-card">
          <div className="character-header">
            <h2>{character.name}</h2>
            <div className="character-level">Level {character.level}</div>
          </div>
          <div className="character-class-race">
            <span className="character-class">{character.class}</span>
            <span className="character-race">{character.race}</span>
          </div>
        </div>

        <div className="stats-grid">
          <div className="stats-section">
            <h3>Base Stats</h3>
            <div className="stats-comparison">
              <div className="stat-row">
                <span className="stat-label">Vitality:</span>
                <span className="stat-base">{fullStats.baseStats.vitality}</span>
                {fullStats.passiveBonuses.vitality > 0 && (
                  <span className="stat-bonus">+{fullStats.passiveBonuses.vitality}</span>
                )}
                <span className="stat-final">= {fullStats.finalStats.vitality}</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Intelligence:</span>
                <span className="stat-base">{fullStats.baseStats.intelligence}</span>
                {fullStats.passiveBonuses.intelligence > 0 && (
                  <span className="stat-bonus">+{fullStats.passiveBonuses.intelligence}</span>
                )}
                <span className="stat-final">= {fullStats.finalStats.intelligence}</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Strength:</span>
                <span className="stat-base">{fullStats.baseStats.strength}</span>
                {fullStats.passiveBonuses.strength > 0 && (
                  <span className="stat-bonus">+{fullStats.passiveBonuses.strength}</span>
                )}
                <span className="stat-final">= {fullStats.finalStats.strength}</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Dexterity:</span>
                <span className="stat-base">{fullStats.baseStats.dexterity}</span>
                {fullStats.passiveBonuses.dexterity > 0 && (
                  <span className="stat-bonus">+{fullStats.passiveBonuses.dexterity}</span>
                )}
                <span className="stat-final">= {fullStats.finalStats.dexterity}</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Constitution:</span>
                <span className="stat-base">{fullStats.baseStats.constitution}</span>
                {fullStats.passiveBonuses.constitution > 0 && (
                  <span className="stat-bonus">+{fullStats.passiveBonuses.constitution}</span>
                )}
                <span className="stat-final">= {fullStats.finalStats.constitution}</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Wisdom:</span>
                <span className="stat-base">{fullStats.baseStats.wisdom}</span>
                {fullStats.passiveBonuses.wisdom > 0 && (
                  <span className="stat-bonus">+{fullStats.passiveBonuses.wisdom}</span>
                )}
                <span className="stat-final">= {fullStats.finalStats.wisdom}</span>
              </div>
            </div>
          </div>

          <div className="stats-section">
            <h3>Derived Stats</h3>
            <div className="derived-stats">
              <div className="stat-row">
                <span className="stat-label">Health Points:</span>
                <span className="stat-final">{fullStats.derivedStats.currentHP} / {fullStats.derivedStats.maxHP}</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Action Points:</span>
                <span className="stat-final">{fullStats.derivedStats.actionPoints}</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Movement:</span>
                <span className="stat-final">{fullStats.derivedStats.movementPoints}</span>
              </div>
            </div>
          </div>

          <div className="stats-section">
            <h3>Combat Stats</h3>
            <div className="combat-stats">
              <div className="stat-row">
                <span className="stat-label">Critical Hit Chance:</span>
                <span className="stat-final">{fullStats.derivedStats.criticalHitChance}%</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Critical Hit Damage:</span>
                <span className="stat-final">{fullStats.derivedStats.criticalHitDamage}%</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Accuracy:</span>
                <span className="stat-final">{fullStats.derivedStats.accuracy}%</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Evasion:</span>
                <span className="stat-final">{fullStats.derivedStats.evasion}%</span>
              </div>
              {fullStats.combatBonuses.skillDamageBonus > 0 && (
                <div className="stat-row">
                  <span className="stat-label">Skill Damage Bonus:</span>
                  <span className="stat-final">+{fullStats.combatBonuses.skillDamageBonus}%</span>
                </div>
              )}
              {fullStats.combatBonuses.attackDamageBonus > 0 && (
                <div className="stat-row">
                  <span className="stat-label">Attack Damage Bonus:</span>
                  <span className="stat-final">+{fullStats.combatBonuses.attackDamageBonus}%</span>
                </div>
              )}
              {fullStats.combatBonuses.healingBonus > 0 && (
                <div className="stat-row">
                  <span className="stat-label">Healing Bonus:</span>
                  <span className="stat-final">+{fullStats.combatBonuses.healingBonus}%</span>
                </div>
              )}
              {fullStats.combatBonuses.blockChance > 0 && (
                <div className="stat-row">
                  <span className="stat-label">Block Chance:</span>
                  <span className="stat-final">{fullStats.combatBonuses.blockChance}%</span>
                </div>
              )}
              {fullStats.combatBonuses.flatDamageReduction > 0 && (
                <div className="stat-row">
                  <span className="stat-label">Damage Reduction:</span>
                  <span className="stat-final">-{fullStats.combatBonuses.flatDamageReduction}</span>
                </div>
              )}
            </div>
          </div>

          {fullStats.damageResistances.length > 0 && (
            <div className="stats-section">
              <h3>Damage Resistances</h3>
              <div className="resistance-stats">
                {fullStats.damageResistances.map(resistance => (
                  <div key={resistance.damageType} className="stat-row">
                    <span className="stat-label">{resistance.damageType}:</span>
                    <span className="stat-final">{resistance.resistancePercent}% resistance</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {fullStats.passiveAbilities.length > 0 && (
            <div className="stats-section">
              <h3>Active Passive Abilities</h3>
              <div className="passive-abilities">
                {fullStats.passiveAbilities.map(ability => (
                  <div key={ability} className="passive-ability-badge">
                    {ability.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </div>
                ))}
              </div>
            </div>
          )}

          {townBonuses.length > 0 && (
            <div className="stats-section">
              <h3>Town Bonuses</h3>
              <p className="town-bonuses-description">Bonuses from liberated towns apply globally to all your characters.</p>
              <div className="town-bonuses-list">
                {townBonuses.map((bonus, idx) => (
                  <div key={idx} className="town-bonus-card">
                    <div className="town-bonus-header">
                      <span className="town-name">{bonus.townName}</span>
                      <span className="town-type-badge">{bonus.townType}</span>
                    </div>
                    <div className="town-bonus-details">
                      <strong>{bonus.bonusName}</strong>
                      <span className="bonus-value">
                        {bonus.bonusType.includes('Percent') || bonus.bonusType.includes('Chance') || bonus.bonusType.includes('Multiplier')
                          ? `+${bonus.bonusValue}%`
                          : `+${bonus.bonusValue}`}
                      </span>
                    </div>
                    <p className="town-bonus-description">{bonus.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderEvolutionTab = () => {
    if (!character) return null;

    if (character.evolvedClass) {
      return (
        <div className="evolution-container">
          <div className="evolution-current-card">
            <h2>⭐ {character.evolvedClass}</h2>
            <p>{character.name} has evolved beyond a base {character.class}.</p>
            {character.evolvedAt && (
              <p className="evolution-date">Evolved on {new Date(character.evolvedAt).toLocaleDateString()}</p>
            )}
          </div>
        </div>
      );
    }

    if (evolutionsLoading) {
      return <div className="loading-spinner">Loading evolution paths...</div>;
    }

    if (evolutions.length === 0) {
      return <p className="evolution-none">No class evolutions are available for {character.class}.</p>;
    }

    return (
      <div className="evolution-container">
        <p className="evolution-intro">
          At level {evolutions[0].requiredLevel}+, a {character.class} can evolve into one of the advanced classes below.
          Evolution is permanent and grants a lasting stat bonus.
        </p>
        {evolutionError && (
          <div className="evolution-error">
            {evolutionError}
            <button onClick={() => setEvolutionError(null)} className="close-error">×</button>
          </div>
        )}
        <div className="evolution-paths">
          {evolutions.map(evolution => (
            <div key={evolution.evolutionId} className={`evolution-card ${evolution.isEligible ? 'eligible' : 'locked'}`}>
              <h3>{evolution.advancedClass}</h3>
              <p className="evolution-description">{evolution.description}</p>

              <div className="evolution-requirements">
                <div className={`requirement-row ${character.level >= evolution.requiredLevel ? 'met' : 'unmet'}`}>
                  {character.level >= evolution.requiredLevel ? '✓' : '✗'} Level {evolution.requiredLevel}
                </div>
                <div className={`requirement-row ${!evolution.unmetReasons.some(r => r.includes('talent points')) ? 'met' : 'unmet'}`}>
                  {!evolution.unmetReasons.some(r => r.includes('talent points')) ? '✓' : '✗'} {evolution.requiredTalentPoints} talent points spent
                </div>
                {evolution.requiredSkills.map(skill => (
                  <div key={skill} className={`requirement-row ${!evolution.missingSkills.includes(skill) ? 'met' : 'unmet'}`}>
                    {!evolution.missingSkills.includes(skill) ? '✓' : '✗'} Skill: {skill}
                  </div>
                ))}
                {Object.entries(evolution.requiredStats).map(([stat, value]) => (
                  <div key={stat} className={`requirement-row ${!evolution.missingStats[stat] ? 'met' : 'unmet'}`}>
                    {!evolution.missingStats[stat] ? '✓' : '✗'} {stat} {value}
                  </div>
                ))}
              </div>

              {Object.keys(evolution.statBonuses).length > 0 && (
                <div className="evolution-bonuses">
                  <strong>Stat Bonuses:</strong>{' '}
                  {Object.entries(evolution.statBonuses).map(([stat, amount]) => `+${amount} ${stat}`).join(', ')}
                </div>
              )}
              {evolution.abilities.length > 0 && (
                <ul className="evolution-abilities">
                  {evolution.abilities.map(ability => <li key={ability}>{ability}</li>)}
                </ul>
              )}

              <button
                className="action-btn evolve-btn"
                disabled={!evolution.isEligible || evolvingId !== null}
                onClick={() => handleEvolve(evolution)}
              >
                {evolvingId === evolution.evolutionId ? 'Evolving...' : `Evolve into ${evolution.advancedClass}`}
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="loading-spinner">Loading character progression...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="character-progression-container">
      {confirmDialog}
      <div className="progression-header">
        {onBack && (
          <button className="back-to-characters-button" onClick={onBack}>
            ← Back to Characters
          </button>
        )}
        <div className="progression-tabs">
          <button
            className={`tab-button ${activeTab === 'skills' ? 'active' : ''}`}
            onClick={() => setActiveTab('skills')}
          >
            Skill Tree
          </button>
          <button
            className={`tab-button ${activeTab === 'stats' ? 'active' : ''}`}
            onClick={() => setActiveTab('stats')}
          >
            Character Stats
          </button>
          <button
            className={`tab-button ${activeTab === 'evolution' ? 'active' : ''}`}
            onClick={() => setActiveTab('evolution')}
          >
            Evolution{character?.evolvedClass ? ` — ${character.evolvedClass}` : ''}
          </button>
        </div>
      </div>

      <div className="tab-content">
        {activeTab === 'skills' && <SkillTree characterId={characterId} onNavigateToSkills={() => setActiveTab('skills')} />}
        {activeTab === 'stats' && renderStatsTab()}
        {activeTab === 'evolution' && renderEvolutionTab()}
      </div>
    </div>
  );
};

export default CharacterProgression;